import { ApiError } from '@/lib/api';
import {
  SETUP_IMPORT_COLUMNS,
  type SetupImportColumnKey,
  type SetupImportRefusal,
} from '@mss_creator/shared';

/**
 * French copy of the Excel import (spec docs/specs/singularity-setup.md,
 * "Import depuis Excel"; catalogue on board 04). The shared module returns
 * typed refusals and the backend machine-readable codes: every sentence the
 * user reads is written here.
 */

/** Why an import cannot be applied at all (refusal box). */
export type SetupImportProblem =
  | { kind: 'refusal'; refusal: SetupImportRefusal }
  | { kind: 'file-too-large'; bytes: number }
  | { kind: 'unsupported-type' }
  | { kind: 'encrypted' }
  | { kind: 'binary-content' };

export interface SetupImportProblemText {
  title: string;
  details: string[];
  fix: string;
}

const FIELD_LABELS: ReadonlyMap<SetupImportColumnKey, string> = new Map(
  SETUP_IMPORT_COLUMNS.map((column) => [column.key, column.canonical] as const),
);

/** Canonical header of a field ("Levier de valeur"), as the editor labels it. */
export function setupFieldLabel(key: SetupImportColumnKey): string {
  return FIELD_LABELS.get(key) ?? key;
}

/** "a", "a et b", "a, b et c". */
export function frenchList(items: readonly string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} et ${items[items.length - 1]}`;
}

/** Singular for 0 and 1, as French counts it. */
export function plural(count: number, singular: string, pluralForm: string): string {
  return `${count} ${count <= 1 ? singular : pluralForm}`;
}

function quote(text: string): string {
  return `« ${text} »`;
}

/** "12 Mo", "5,3 Mo" — one decimal at most, French separator. */
export function formatMegabytes(bytes: number): string {
  const megabytes = Math.round((bytes / (1024 * 1024)) * 10) / 10;
  return `${String(megabytes).replace('.', ',')} Mo`;
}

function tooManyRowsDetails(count: number, extraRowLabels: readonly string[]): string[] {
  const details = [`${count} lignes trouvées, le setup en compte 6.`];
  const labelled = extraRowLabels
    .map((label, index) => ({ line: 7 + index, label }))
    .filter((entry) => entry.label.length > 0);
  if (labelled.length === 1 && labelled[0] !== undefined) {
    details.push(`La ligne ${labelled[0].line} commence par ${quote(labelled[0].label)}.`);
  } else if (labelled.length > 1) {
    details.push(
      `Les lignes ${frenchList(labelled.map((entry) => String(entry.line)))} commencent par ${frenchList(labelled.map((entry) => quote(entry.label)))}.`,
    );
  }
  return details;
}

function invalidStepDetail(invalidValues: readonly string[], duplicateSteps: readonly number[]) {
  const parts: string[] = [];
  for (const step of duplicateSteps) {
    parts.push(`${quote(String(step))} apparaît plusieurs fois`);
  }
  const invalid = invalidValues.map((value) => (value.length > 0 ? quote(value) : 'une case vide'));
  if (invalid.length === 1) parts.push(`${frenchList(invalid)} n'existe pas`);
  if (invalid.length > 1) parts.push(`${frenchList(invalid)} n'existent pas`);
  return `Colonne Étape : ${parts.join(', ')}.`;
}

export function describeSetupImportProblem(problem: SetupImportProblem): SetupImportProblemText {
  switch (problem.kind) {
    case 'file-too-large':
      return {
        title: 'Fichier trop volumineux',
        details: [`${formatMegabytes(problem.bytes)}, la limite est de 5 Mo.`],
        fix: 'Copiez-collez plutôt les cellules du tableau.',
      };
    case 'unsupported-type':
      return {
        title: 'Format non pris en charge',
        details: ['Seuls les fichiers .xlsx sont acceptés.'],
        fix: 'Pour un CSV ou un autre tableur, copiez-collez les cellules.',
      };
    case 'encrypted':
      return {
        title: 'Fichier illisible',
        details: ['Ce fichier est protégé par mot de passe, ou au format Excel ancien (.xls).'],
        fix: 'Enregistrez-le en .xlsx sans mot de passe, ou copiez-collez les cellules.',
      };
    case 'binary-content':
      return {
        title: 'Fichier illisible',
        details: ["Ce fichier est corrompu ou n'est pas un classeur Excel lisible."],
        fix: 'Enregistrez-le à nouveau en .xlsx depuis Excel, ou copiez-collez les cellules.',
      };
    case 'refusal':
      return describeRefusal(problem.refusal);
  }
}

function describeRefusal(refusal: SetupImportRefusal): SetupImportProblemText {
  switch (refusal.kind) {
    case 'no-header':
      return {
        title: 'Aucun en-tête reconnu',
        details: [
          'La première ligne doit contenir au moins un nom de colonne du setup (Driver, Levier de valeur, Méthode…).',
        ],
        fix: "Copiez la ligne d'en-têtes avec les données, ou partez du modèle.",
      };
    case 'no-table':
      return {
        title: 'Aucun tableau de setup reconnu dans ce fichier',
        details: [
          'Aucune feuille ne contient, dans ses 20 premières lignes, une ligne avec au moins 2 en-têtes du setup.',
        ],
        fix: 'Vérifiez le fichier, ou partez du modèle.',
      };
    case 'too-many-rows':
      return {
        title: 'Plus de 6 lignes de données',
        details: tooManyRowsDetails(refusal.count, refusal.extraRowLabels),
        fix: 'Ne gardez que les 6 étapes.',
      };
    case 'invalid-step':
      return {
        title: 'Étape invalide ou en double',
        details: [invalidStepDetail(refusal.invalidValues, refusal.duplicateSteps)],
        fix: "Numérotez les étapes de 1 à 6, une fois chacune, ou retirez la colonne Étape pour suivre l'ordre des lignes.",
      };
    case 'duplicate-column':
      return {
        title: 'Colonne en double',
        details: [`${frenchList(refusal.headers.map(quote))} désignent le même champ.`],
        fix:
          refusal.headers.length > 2
            ? 'Gardez une seule de ces colonnes.'
            : 'Gardez une seule des deux colonnes.',
      };
  }
}

/**
 * Maps an upload error to a refusal, or `null` when it is a transient
 * failure (network, server, rights) that "Réessayer" may fix.
 */
export function problemFromUploadError(error: unknown, file: File): SetupImportProblem | null {
  if (!(error instanceof ApiError)) return null;
  if (error.status === 413 || error.code === 'SETUP_IMPORT_FILE_TOO_LARGE') {
    return { kind: 'file-too-large', bytes: file.size };
  }
  if (error.code === 'FILE_UNSUPPORTED_TYPE') return { kind: 'unsupported-type' };
  if (error.code === 'FILE_ENCRYPTED') return { kind: 'encrypted' };
  if (error.code === 'FILE_BINARY_CONTENT') return { kind: 'binary-content' };
  return null;
}

/** Client-side checks run before uploading, so the user gets instant feedback. */
export function problemBeforeUpload(file: File, maxBytes: number): SetupImportProblem | null {
  if (!file.name.toLowerCase().endsWith('.xlsx')) return { kind: 'unsupported-type' };
  if (file.size > maxBytes) return { kind: 'file-too-large', bytes: file.size };
  return null;
}

/** Transient failure copy: network, server, or a 403 on the import route. */
export function describeUploadFailure(error: unknown): string {
  if (error instanceof ApiError && error.status === 403) {
    return "Vous n'avez pas le droit d'importer le setup de cette entreprise.";
  }
  return "Le fichier n'a pas pu être lu : erreur réseau ou serveur. Réessayez, ou changez de source.";
}
